import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useCallback, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'

export type ModalVariant = 'default' | 'danger'

const VARIANT_TITLE_COLOR: Record<ModalVariant, string> = {
  default: 'text-black',
  danger: 'text-danger',
}

const VARIANT_CLOSE_BUTTON_HOVER: Record<ModalVariant, string> = {
  default: 'hover:text-pink-500 focus:ring-pink-500',
  danger: 'hover:text-danger focus:ring-danger',
}

const FOCUS_SELECTOR =
  "button, a, input, select, textarea, [tabindex]:not([tabindex='-1'])"

const getFocusable = (container: HTMLElement | null): Array<HTMLElement> =>
  container
    ? Array.from(
        container.querySelectorAll<HTMLElement>(FOCUS_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1)
    : []

const DEFAULT_OVERLAY_CLASSNAME =
  'fixed inset-0 z-50 flex items-center justify-center px-8 backdrop-blur-md bg-white/40'
const DEFAULT_CARD_CLASSNAME =
  'bg-black-50 rounded-2xl shadow-2xl max-w-md w-full p-8 relative'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  closeLabel: string
  children: ReactNode
  variant?: ModalVariant
  icon?: ReactNode
  title?: ReactNode
  overlayClassName?: string
  cardClassName?: string
  closeOnOverlayClick?: boolean
  hideCloseButton?: boolean
}

export const Modal = ({
  isOpen,
  onClose,
  closeLabel,
  children,
  variant = 'default',
  icon,
  title,
  overlayClassName = DEFAULT_OVERLAY_CLASSNAME,
  cardClassName = DEFAULT_CARD_CLASSNAME,
  closeOnOverlayClick = true,
  hideCloseButton = false,
}: ModalProps) => {
  const cardRef = useRef<HTMLDivElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return

      if (e.key === 'Escape') {
        onClose()
        return
      }

      if (e.key !== 'Tab') return

      const focusables = getFocusable(cardRef.current)
      if (focusables.length === 0) return

      const first = focusables[0]
      const last = focusables[focusables.length - 1]

      // Cycle du focus
      if (e.shiftKey && document.activeElement === first) {
        last.focus()
        e.preventDefault()
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus()
        e.preventDefault()
      }
    },
    [isOpen, onClose],
  )

  useEffect(() => {
    if (!isOpen) return

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleKeyDown])

  useEffect(() => {
    if (!isOpen) return

    // Mémoriser l'élément actif et focus initial — ne doit s'exécuter qu'à
    // l'ouverture, pas à chaque re-render (sinon la frappe au clavier dans un
    // champ du modal renvoie le focus sur le bouton fermer à chaque touche).
    previousActiveElement.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null

    if (!hideCloseButton && closeButtonRef.current) {
      closeButtonRef.current.focus()
    } else {
      const first = getFocusable(cardRef.current)[0]
      if (first instanceof HTMLElement) first.focus()
    }

    return () => {
      // Restaure le focus précédent
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus()
      }
    }
  }, [isOpen])

  if (!isOpen) return null

  return createPortal(
    <div
      className={overlayClassName}
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      <div
        className={cardClassName}
        ref={cardRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {!hideCloseButton && (
          <button
            ref={closeButtonRef}
            className={`absolute top-4 right-4 text-2xl text-gray-400 focus:outline-none focus:ring-2 flex items-center justify-center bg-transparent z-10 cursor-pointer ${VARIANT_CLOSE_BUTTON_HOVER[variant]}`}
            onClick={onClose}
            aria-label={closeLabel}
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {title && (
          <h2
            id="modal-title"
            className={`flex items-center gap-3 text-2xl font-bold mb-5.75 ${VARIANT_TITLE_COLOR[variant]}`}
          >
            {icon}
            {title}
          </h2>
        )}
        {children}
      </div>
    </div>,
    document.body,
  )
}
